import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Users, Building2, AlertOctagon, CheckCircle } from "lucide-react";
import { useDeploymentStats } from "../adapters/Requests"; // New Hook
import { Loader2 } from "lucide-react";

// --- Tooltip & Sub-components ---

const MinimalTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white px-3 py-2 rounded border border-gray-100 shadow-lg z-50">
        <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
          {payload[0].payload.name}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: entry.color || entry.fill }}
            />
            <p className="text-sm font-bold text-gray-900">
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const ChartCard = ({ title, children, footerData, hasData }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col h-full shadow-sm hover:shadow-md transition-shadow duration-200">
    <div className="mb-4">
      <h3 className="text-sm font-bold text-gray-700">{title}</h3>
    </div>
    <div className="flex-1 min-h-[180px] w-full relative">
      {!hasData ? (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-300">
          No data available
        </div>
      ) : (
        <ResponsiveContainer
          width="100%"
          height="100%"
          className="!outline-none"
        >
          {children}
        </ResponsiveContainer>
      )}
    </div>
    {footerData && (
      <div className="mt-4 pt-3 border-t border-gray-50 flex flex-wrap gap-3 justify-center">
        {footerData.map((item, idx) => (
          <div key={idx} className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-[10px] font-semibold text-gray-500 uppercase">
              {item.name}
            </span>
            <span className="text-[10px] font-bold text-gray-800">
              {item.value}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

function SmallStatGrid({ stats }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
      {stats.map((stat, index) => (
        <div
          key={index}
          className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 bg-slate-50 shadow-sm cursor-default select-none h-24 ${
            stat.alert ? "border-red-100" : "border-white"
          }`}
        >
          <div
            className={`h-7 w-7 rounded-md flex items-center justify-center mb-2 ${
              stat.alert ? "text-red-500" : "text-emerald-500"
            }`}
          >
            <stat.icon size={18} />
          </div>
          <h3 className="text-lg font-bold text-slate-700 leading-none">
            {stat.value}
          </h3>
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mt-1.5">
            {stat.label}
          </p>
        </div>
      ))}
    </div>
  );
}

// --- Main Component ---

export default function DeptRepAnalytics({ dateRange }) {
  // Use the new Pipeline hook
  const { data, isLoading } = useDeploymentStats(dateRange);

  if (isLoading) {
    return (
      <div className="w-full h-64 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  // Extract data from the pipeline response
  const metrics = data?.metrics || {
    unassigned: 0,
    assigned: 0,
    total: 0,
    activeDepts: 0,
  };
  const chartData = data?.chartData || [];

  // Prepare Pie Chart Data
  const assignmentData = [
    { name: "Assigned", value: metrics.assigned, color: "#10b981" },
    { name: "Unassigned", value: metrics.unassigned, color: "#ef4444" },
  ].filter((d) => d.value > 0);

  const stats = [
    {
      label: "Unassigned",
      value: metrics.unassigned,
      icon: AlertOctagon,
      alert: metrics.unassigned > 0,
    },
    { label: "Assigned", value: metrics.assigned, icon: CheckCircle },
    { label: "Eligible Pool", value: metrics.total, icon: Users },
    { label: "Active Units", value: metrics.activeDepts, icon: Building2 },
  ];

  const axisStyle = { fill: "#9ca3af", fontSize: 10, fontWeight: 500 };
  const gridStyle = { stroke: "#f3f4f6", strokeDasharray: "3 3" };

  return (
    <div className="space-y-6">
      <style>{`
        .recharts-responsive-container:focus { outline: none !important; }
        path:focus { outline: none !important; }
      `}</style>

      <div>
        <div className="flex items-center justify-between mb-3 -mt-1">
          <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">
            Deployment Overview
          </h2>
        </div>
        <SmallStatGrid stats={stats} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Chart: Department Load */}
        <div className="lg:col-span-2">
          <ChartCard
            title="Department Load & Assignment"
            hasData={chartData.length > 0}
            footerData={[
              { name: "Assigned", value: metrics.assigned, color: "#10b981" },
              { name: "Pending", value: metrics.unassigned, color: "#cbd5e1" },
            ]}
          >
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 0, left: -20, bottom: 0 }}
            >
              <CartesianGrid {...gridStyle} vertical={false} />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={axisStyle}
                dy={10}
                interval={0} // Force show all labels
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={axisStyle}
                allowDecimals={false}
              />
              <Tooltip
                content={<MinimalTooltip />}
                cursor={{ fill: "transparent" }}
              />
              <Bar
                dataKey="assigned"
                stackId="a"
                fill="#10b981"
                radius={[0, 0, 0, 0]}
                barSize={30}
                name="Assigned"
              />
              <Bar
                dataKey="pending"
                stackId="a"
                fill="#cbd5e1"
                radius={[3, 3, 0, 0]}
                barSize={30}
                name="Pending"
              />
            </BarChart>
          </ChartCard>
        </div>

        {/* Side Chart: Progress Pie */}
        <div className="lg:col-span-1">
          <ChartCard
            title="Deployment Progress"
            hasData={assignmentData.length > 0}
            footerData={assignmentData}
          >
            <PieChart>
              <Pie
                data={assignmentData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
              >
                {assignmentData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<MinimalTooltip />} />
              <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                <tspan
                  x="50%"
                  dy="-0.5em"
                  fontSize="24"
                  fontWeight="bold"
                  fill="#1f2937"
                >
                  {metrics.total > 0
                    ? Math.round((metrics.assigned / metrics.total) * 100)
                    : 0}
                  %
                </tspan>
                <tspan
                  x="50%"
                  dy="2.0em"
                  fontSize="10"
                  fontWeight="500"
                  fill="#9ca3af"
                >
                  DEPLOYED
                </tspan>
              </text>
            </PieChart>
          </ChartCard>
        </div>
      </div>
    </div>
  );
}
